import { FormattedNumber, useIntl } from 'react-intl'

import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import type { AffiliatePartnerLeaderboardRowSchema } from '@/api/gen'
import { DeltaIndicator } from '@/components/delta-indicator'
import { fluidColumn } from '@/components/table-columns'
import { TierTag } from '@/components/tier-tag'
import { toAmount } from '@/utils/to-amount'
import { toRate } from '@/utils/to-rate'

export const useLeaderboardColumns = (): ColumnDef<AffiliatePartnerLeaderboardRowSchema>[] => {
  const { formatMessage } = useIntl()

  return createColumns<AffiliatePartnerLeaderboardRowSchema>([
    {
      accessorKey: 'partner_name',
      header: formatMessage({ id: 'admin.partners.name' }),
      ...fluidColumn({ min: 160, flex: 1.4, stacked: true }),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex flex-col">
          {row.original.partner_name}
          <Typography
            color="text.secondary"
            variant="caption"
          >
            {row.original.partner_email}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: 'tier_name',
      header: formatMessage({ id: 'admin.partners.tier' }),
      ...fluidColumn({ min: 110, flex: 0.8 }),
      enableSorting: false,
      cell: ({ row }) => (
        <TierTag
          commissionRate={row.original.commission_rate}
          tierName={row.original.tier_name}
        />
      ),
    },
    {
      accessorKey: 'revenue_usd',
      header: formatMessage({ id: 'admin.partners.revenue' }),
      ...fluidColumn({ min: 100, flex: 0.7, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => (
        <FormattedNumber
          currency="USD"
          maximumFractionDigits={0}
          style="currency"
          value={toAmount(row.original.revenue_usd, 'leaderboard revenue_usd:')}
        />
      ),
    },
    {
      accessorKey: 'commission_usd',
      header: formatMessage({ id: 'admin.partners.commissions' }),
      ...fluidColumn({ min: 100, flex: 0.7, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => (
        <Typography
          color="primary"
          variant="body2"
        >
          <FormattedNumber
            currency="USD"
            maximumFractionDigits={0}
            style="currency"
            value={toAmount(row.original.commission_usd, 'leaderboard commission_usd:')}
          />
        </Typography>
      ),
    },
    {
      accessorKey: 'delta_pct',
      header: formatMessage({ id: 'admin.dashboard.deltaVsPrev' }),
      ...fluidColumn({ min: 90, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => (
        <DeltaIndicator
          value={
            row.original.delta_pct == null
              ? null
              : toRate(row.original.delta_pct, 'leaderboard delta_pct:')
          }
        />
      ),
    },
  ])
}
